<script setup lang="ts">
import { computed } from 'vue'
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { ClockIcon } from '@ecx/ui/icons'
import { useActiveBulkImportsStore } from '@/stores/activeBulkImports'
import { useBulkImportStore } from '@/stores/bulkImport'
import { isPartialCompletion } from '@/composables/bulkImport/useBulkImportStatus'
import { isActiveTerminal } from '@/types/activeBulkImport'
import type { ActiveBulkImport } from '@/types/activeBulkImport'
import type { BulkImportEntity } from '@/types/bulkImport'
import BulkImportMinimizedToast from './BulkImportMinimizedToast.vue'
import type { BulkImportToastVariant } from './BulkImportMinimizedToast.vue'

const emit = defineEmits<{
  (e: 'view-progress', item: ActiveBulkImport): void
  (e: 'reopen', item: ActiveBulkImport): void
}>()

const { t } = useI18n()
const store = useActiveBulkImportsStore()
const bulkStore = useBulkImportStore()

const visible = computed(() =>
  store.imports.filter(
    (i) => !i.dismissed && !(bulkStore.drawerOpen && i.id === bulkStore.currentImport?.id),
  ),
)

interface ToastMeta {
  variant: BulkImportToastVariant
  title: string
  description: string
  primaryLabel: string
  icon?: Component
}

function editDescription(phase: 'processing' | 'completed', entity: BulkImportEntity): string {
  const base = `corp.bulk_import.toast.edit_${phase}_description`
  if (entity === 'vehicles') return t(`${base}_vehicles`)
  if (entity === 'cards') return t(`${base}_cards`)
  return t(base)
}

function resolveVariant(item: ActiveBulkImport): ToastMeta {
  const isVehicles = item.entity === 'vehicles'
  const isEditOp = item.operationType === 'edit'

  if (item.errorMessage && !isActiveTerminal(item.status)) {
    return {
      variant: 'error',
      title: t('corp.bulk_import.toast.poll_failed_title'),
      description: item.errorMessage,
      primaryLabel: t('corp.bulk_import.toast.btn_check_progress'),
    }
  }

  if (item.status === 'failed') {
    return {
      variant: 'error',
      title: t('corp.bulk_import.toast.failed_title'),
      description: t('corp.bulk_import.toast.failed_description'),
      primaryLabel: t('corp.bulk_import.toast.btn_view_details'),
    }
  }

  if (item.status === 'invalid') {
    return {
      variant: 'error',
      title: t('corp.bulk_import.toast.invalid_title'),
      description: t('corp.bulk_import.toast.invalid_description'),
      primaryLabel: t('corp.bulk_import.toast.btn_view_errors'),
    }
  }

  if (item.status === 'completed') {
    if (isEditOp && isPartialCompletion(item.totals?.failed ?? 0, item.hasCardFailures)) {
      return {
        variant: 'partial',
        title: t('corp.bulk_import.toast.edit_partial_title'),
        description: t('corp.bulk_import.toast.edit_partial_description'),
        primaryLabel: t('corp.bulk_import.toast.btn_view_summary'),
      }
    }

    return {
      variant: 'completed',
      title: isEditOp
        ? t('corp.bulk_import.toast.edit_completed_title')
        : t('corp.bulk_import.toast.completed_title'),
      description: isEditOp
        ? editDescription('completed', item.entity)
        : isVehicles
          ? t('corp.bulk_import.toast.completed_description_vehicles')
          : t('corp.bulk_import.toast.completed_description'),
      primaryLabel: t('corp.bulk_import.toast.btn_view_summary'),
    }
  }

  if (isEditOp) {
    return {
      variant: 'processing',
      title: t('corp.bulk_import.toast.edit_processing_title'),
      description: editDescription('processing', item.entity),
      primaryLabel: t('corp.bulk_import.toast.btn_check_progress'),
      icon: ClockIcon,
    }
  }

  return {
    variant: 'processing',
    title: isVehicles
      ? t('corp.bulk_import.toast.processing_title_vehicles')
      : t('corp.bulk_import.toast.processing_title'),
    description: isVehicles
      ? t('corp.bulk_import.toast.processing_description_vehicles')
      : t('corp.bulk_import.toast.processing_description'),
    primaryLabel: t('corp.bulk_import.toast.btn_check_progress'),
  }
}

function handleClose(item: ActiveBulkImport) {
  store.dismiss(item.id)
}

function handlePrimary(item: ActiveBulkImport) {
  if (isActiveTerminal(item.status)) {
    emit('reopen', item)
  } else {
    emit('view-progress', item)
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed bottom-6 right-6 z-bulk-import-toast flex flex-col-reverse gap-3"
    >
      <TransitionGroup name="bulk-toast">
        <BulkImportMinimizedToast
          v-for="item in visible"
          :key="item.id"
          v-bind="resolveVariant(item)"
          :close-label="t('corp.bulk_import.toast.btn_close')"
          @close="handleClose(item)"
          @primary="handlePrimary(item)"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>
